import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import { useWorkspaceFiles } from 'api/files';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { uploadFileToWorkspace } from 'api/upload';
import { useNotifications } from 'components/notify';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { useSnackbarSurface } from './SnackbarRenderProvider';

export type UploadStatusCode = 'pending' | 'uploading' | 'completed' | 'error';

export type UploadEntry = {
  id: string;
  fileName: string;
  fileSize: number;
  progress: number;
  status: string;
  statusCode: UploadStatusCode;
  error?: string;
  startedAt: number;
};

type UploadState = {
  uploads: UploadEntry[];
};

type UploadContextValue = UploadState & {
  beginUpload: (file: File) => void;
  beginUploadMany: (files: File[]) => void;
  isUploading: boolean;
  clearCompleted: () => void;
  showUploadsModal: () => void;
};

const UploadContext = React.createContext<UploadContextValue | null>(null);

function nextId() {
  return `upload-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function UploadProvider(props: React.PropsWithChildren) {
  const surface = useSnackbarSurface();
  const { children } = props;
  const workspaceId = useWorkspaceId();
  const { mutate: refreshFiles } = useWorkspaceFiles();
  const { showNotification } = useNotifications();
  const [uploads, setUploads] = useState<UploadEntry[]>([]);
  const [uploadsModalOpen, setUploadsModalOpen] = useState(false);
  const inFlightRef = useRef<Set<string>>(new Set());

  const showUploadsModal = useCallback(() => {
    setUploadsModalOpen(true);
  }, []);

  const isUploading = useMemo(
    () =>
      uploads.some(
        (u) => u.statusCode === 'pending' || u.statusCode === 'uploading',
      ),
    [uploads],
  );

  console.log('isUploading', isUploading);
  surface(
    isUploading ? (
      <List dense disablePadding>
        {uploads.map((u) => (
          <ListItem
            key={u.id}
            alignItems="flex-start"
            disablePadding
            sx={{ flexDirection: 'column', alignItems: 'stretch', mb: 1 }}
          >
            <ListItemText
              primary={u.fileName}
              secondary={
                <>
                  <Typography
                    variant="caption"
                    component="span"
                    display="block"
                  >
                    {u.status}
                  </Typography>
                  {(u.statusCode === 'uploading' ||
                    u.statusCode === 'pending') && (
                    <LinearProgress
                      variant="determinate"
                      value={u.progress}
                      sx={{ mt: 0.5 }}
                    />
                  )}
                  {u.error && (
                    <Typography
                      variant="caption"
                      color="error"
                      component="span"
                      display="block"
                    >
                      {u.error}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        ))}
      </List>
    ) : null,
  );

  const runOneUpload = useCallback(
    async (entry: UploadEntry, file: File) => {
      if (!workspaceId) {
        setUploads((prev) =>
          prev.map((u) =>
            u.id === entry.id
              ? {
                  ...u,
                  statusCode: 'error' as const,
                  status: 'No workspace selected',
                  error: 'No workspace selected',
                }
              : u,
          ),
        );
        return;
      }

      setUploads((prev) =>
        prev.map((u) =>
          u.id === entry.id ? { ...u, statusCode: 'uploading' as const } : u,
        ),
      );

      try {
        await uploadFileToWorkspace(workspaceId, file, {
          onProgress: (progress) => {
            setUploads((prev) =>
              prev.map((u) => (u.id === entry.id ? { ...u, progress } : u)),
            );
          },
          onStatus: (status) => {
            setUploads((prev) =>
              prev.map((u) => (u.id === entry.id ? { ...u, status } : u)),
            );
          },
        });

        setUploads((prev) =>
          prev.map((u) =>
            u.id === entry.id
              ? {
                  ...u,
                  statusCode: 'completed' as const,
                  progress: 100,
                  status: 'Upload completed!',
                }
              : u,
          ),
        );
        showNotification(`File "${file.name}" uploaded successfully!`, {
          variant: 'success',
        });
        refreshFiles();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Upload failed';
        setUploads((prev) =>
          prev.map((u) =>
            u.id === entry.id
              ? {
                  ...u,
                  statusCode: 'error' as const,
                  status: message,
                  error: message,
                }
              : u,
          ),
        );
        showNotification(message, { variant: 'error' });
      } finally {
        inFlightRef.current.delete(entry.id);
      }
    },
    [workspaceId, showNotification, refreshFiles],
  );

  const beginUpload = useCallback(
    (file: File) => {
      const id = nextId();
      const entry: UploadEntry = {
        id,
        fileName: file.name,
        fileSize: file.size,
        progress: 0,
        status: 'Pending...',
        statusCode: 'pending',
        startedAt: Date.now(),
      };
      setUploads((prev) => [...prev, entry]);
      inFlightRef.current.add(id);
      runOneUpload(entry, file);
    },
    [runOneUpload],
  );

  const beginUploadMany = useCallback(
    (files: File[]) => {
      files.forEach((file) => beginUpload(file));
    },
    [beginUpload],
  );

  const clearCompleted = useCallback(() => {
    setUploads((prev) =>
      prev.filter(
        (u) => u.statusCode === 'uploading' || u.statusCode === 'pending',
      ),
    );
  }, []);

  const value = useMemo<UploadContextValue>(
    () => ({
      uploads,
      beginUpload,
      beginUploadMany,
      isUploading,
      clearCompleted,
      showUploadsModal,
    }),
    [
      uploads,
      beginUpload,
      beginUploadMany,
      isUploading,
      clearCompleted,
      showUploadsModal,
    ],
  );

  return (
    <UploadContext.Provider value={value}>
      {children}
      <Dialog
        open={uploadsModalOpen}
        onClose={() => setUploadsModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Uploads</DialogTitle>
        <DialogContent>
          {uploads.length === 0 ? (
            <Typography color="text.secondary">
              No uploads in this session.
            </Typography>
          ) : (
            <List dense disablePadding>
              {uploads.map((u) => (
                <ListItem
                  key={u.id}
                  alignItems="flex-start"
                  disablePadding
                  sx={{ flexDirection: 'column', alignItems: 'stretch', mb: 1 }}
                >
                  <ListItemText
                    primary={u.fileName}
                    secondary={
                      <>
                        <Typography
                          variant="caption"
                          component="span"
                          display="block"
                        >
                          {u.status}
                        </Typography>
                        {(u.statusCode === 'uploading' ||
                          u.statusCode === 'pending') && (
                          <LinearProgress
                            variant="determinate"
                            value={u.progress}
                            sx={{ mt: 0.5 }}
                          />
                        )}
                        {u.error && (
                          <Typography
                            variant="caption"
                            color="error"
                            component="span"
                            display="block"
                          >
                            {u.error}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={clearCompleted} disabled={isUploading}>
            Clear completed
          </Button>
          <Button onClick={() => setUploadsModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </UploadContext.Provider>
  );
}

export function useUpload() {
  const ctx = React.useContext(UploadContext);
  if (ctx == null) {
    throw new Error('useUpload must be used within UploadProvider');
  }
  return ctx;
}

export function useUploadOptional() {
  return React.useContext(UploadContext);
}
